/**
 * Onboarding Journey overview — /onboarding (M27 PR 1).
 *
 * The canonical detail view of the 9-step setup journey. Replaces
 * the M7 wizard surface at /onboarding (M7 lives at /setup now).
 *
 * Layout:
 *   - Header card: percent complete + estimated time remaining.
 *   - 9 step cards rendered in order, each linking to its own
 *     /onboarding/{slug} page.
 *
 * The page is the journey index. Step pages render their own
 * content; this page is just the map.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Eye, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../../components/Loading";
import { OnboardingStepCard } from "../../components/onboarding/OnboardingStepCard";
import { PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import {
  ONBOARDING_JOURNEY_QUERY_KEY,
  STEP_EST_MINUTES,
  STEP_SLUGS,
  isComplete,
  isDismissed,
  nextActiveStep,
  percentComplete,
  type OnboardingState,
} from "../../lib/onboarding";

export function OnboardingJourneyPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ONBOARDING_JOURNEY_QUERY_KEY,
    queryFn: () => api.getOnboardingJourneyState(),
  });

  // Clear the snooze set by the Mission Control widget's "Hide for
  // 7 days" button. The user is here viewing the journey — that's
  // an explicit signal they want the widget back.
  const unhide = useMutation({
    mutationFn: () =>
      api.updateOnboardingJourneyState({ dismissed_until: null }),
    onSuccess: (next) => qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next),
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const state = q.data as OnboardingState;
  const pct = percentComplete(state);
  const complete = isComplete(state);
  const next = nextActiveStep(state);
  const dismissed = isDismissed(state);

  // Remaining time = sum of est minutes for non-completed/skipped steps.
  const remainingMin = STEP_SLUGS.reduce((acc, s) => {
    const status = state.steps?.[s]?.status;
    if (status === "completed" || status === "skipped") return acc;
    return acc + STEP_EST_MINUTES[s];
  }, 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("app.onb_journey_title")}
        subtitle={t("app.onb_journey_subtitle")}
        breadcrumbs={[{ label: t("app.onb_journey_breadcrumb") }]}
      />

      {/* Unhide affordance — only renders when the Mission Control
          widget is currently snoozed. Clicking restores it so the
          user doesn't have to wait for the 7-day timer. */}
      {!complete && dismissed && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-sunken px-4 py-3 text-sm">
          <span className="text-text-secondary">
            {t("app.onb_dismissed_msg")}
          </span>
          <button
            type="button"
            onClick={() => unhide.mutate()}
            disabled={unhide.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-default/80 disabled:opacity-60"
          >
            <Eye size={12} aria-hidden />
            {unhide.isPending
              ? t("app.onb_restoring")
              : t("app.onb_show_on_mc")}
          </button>
        </div>
      )}

      {/* Progress card — the headline. */}
      <section
        className="relative overflow-hidden rounded-xl border border-ai-subtle bg-ai-bg p-5"
      >
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
        />
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-surface-default text-ai">
            <Sparkles size={20} aria-hidden />
          </span>
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm text-text-secondary">
              {complete
                ? t("app.onb_youre_live")
                : t("app.onb_pct_through", { pct })}
            </p>
            <div
              className="mt-2 h-2 w-full rounded-full bg-surface-default"
              aria-hidden
            >
              <div
                className="h-full rounded-full bg-brand-gradient-h transition-[width] duration-small ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            {!complete && (
              <p className="mt-2 text-xs text-text-tertiary">
                {t("app.onb_est_remaining", {
                  min: remainingMin,
                  stepsLeft:
                    STEP_SLUGS.length -
                    STEP_SLUGS.filter(
                      (s) =>
                        state.steps?.[s]?.status === "completed" ||
                        state.steps?.[s]?.status === "skipped",
                    ).length,
                })}
              </p>
            )}
          </div>
          {!complete && (
            <Link
              to={`/onboarding/${next}`}
              className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
            >
              {pct === 0
                ? t("app.onb_start_journey")
                : t("app.onb_continue")}
              <ArrowRight size={14} aria-hidden className="rtl:rotate-180" />
            </Link>
          )}
        </div>
      </section>

      {/* 9 step cards */}
      <ul className="grid grid-cols-1 gap-3">
        {STEP_SLUGS.map((slug, i) => {
          const step = state.steps?.[slug];
          if (!step) return null;
          return (
            <li key={slug}>
              <OnboardingStepCard
                slug={slug}
                index={i}
                state={step}
                isCurrent={state.current_step === slug}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
